import { all, call, put, select, takeLatest } from "redux-saga/effects";

import { PayloadAction } from "@reduxjs/toolkit";
import { getWorkDocumentsInfo } from "../actions";
import {
  approversCardDocumentListName,
  approversDependenciesListName,
  ListFieldsRecordType,
  myDocumentsListName,
  myWorksDocumentsListName,
  ReplacedListName,
  versionsByDocumentListName,
} from "src/api/config";
import { CurrentUserType } from "src/modules/users/types";
import { getItemsRequest } from "src/modules/spItems/api";
import { DocumentsWithApproversTypeRecord, GetSagaProps, LookupFields, UserFields } from "src/modules/modules.type";
import { getItemsSuccess } from "src/modules/spItems/actions";
import _ from "lodash";
import { getMyDocumentsInfoFailure } from "../../my_documents/actions";
import { selectCurrentUser } from "src/modules/users/selector";
import { docStatuses } from "src/api/config/systemSwitch";

type ApproversCardDocumentMapType = Map<number, ListFieldsRecordType[typeof approversCardDocumentListName][]>;
type ApproversDependenciesType = ListFieldsRecordType[typeof approversDependenciesListName];
type DocumentItem =
  | ListFieldsRecordType[typeof myDocumentsListName]
  | ListFieldsRecordType[typeof versionsByDocumentListName];
type ApproversDependenciesCache = Map<string, ApproversDependenciesType[]>;
type DocumentsCache = Map<number, DocumentItem>;

function mergeMaps(
  map1: ApproversCardDocumentMapType,
  map2: ApproversCardDocumentMapType,
): ApproversCardDocumentMapType {
  const mergedMap: ApproversCardDocumentMapType = new Map();

  for (const [key, value] of map1) {
    mergedMap.set(key, value);
  }

  for (const [key, value] of map2) {
    mergedMap.set(key, value);
  }

  return mergedMap;
}

function getApproversDependenciesKey(docType: number, collection: number): string {
  return `${docType}_${collection}`;
}

/**
 * Приоритет версии над главным документом в итоговой выдаче.
 * Если для главного документа (VersionID === 0) в выдаче уже есть его версия
 * (VersionID === ID главного) — главный документ скрывается, остаётся версия.
 * Работает независимо от того, через какой поток (Flow 1/2/3) каждый из них попал в выдачу.
 */
function applyMainVersionPriority(
  documents: DocumentsWithApproversTypeRecord[],
): DocumentsWithApproversTypeRecord[] {
  const mainDocumentIdsCoveredByVersion = new Set(
    documents.filter((d) => d.VersionID !== 0).map((d) => d.VersionID),
  );

  return documents.filter((d) => d.VersionID !== 0 || !mainDocumentIdsCoveredByVersion.has(d.ID));
}

async function fetchDocumentsByIds(
  approversMap: ApproversCardDocumentMapType,
  versionMap: ApproversCardDocumentMapType,
): Promise<{ cache: DocumentsCache; nextPage: number | null }> {
  const cache: DocumentsCache = new Map();
  const docIds = [...approversMap.keys()];
  const versionIds = [...versionMap.keys()];
  let nextPage: number | null = null;

  const requests: Promise<void>[] = [];

  if (docIds.length > 0) {
    requests.push(
      getItemsRequest({
        listName: myDocumentsListName,
        filters: { ID: docIds },
      }).then((result) => {
        nextPage = result.nextPage;
        result.items.forEach((item) => cache.set(item.ID, item));
      }),
    );
  }

  if (versionIds.length > 0) {
    requests.push(
      getItemsRequest({
        listName: versionsByDocumentListName,
        filters: { ID: versionIds },
      }).then((result) => {
        result.items.forEach((item) => cache.set(item.ID, item));
      }),
    );
  }

  await Promise.all(requests);

  return { cache, nextPage };
}

async function fetchApproversDependenciesCache(
  approversMap: ApproversCardDocumentMapType,
  versionMap: ApproversCardDocumentMapType,
  documentsCache: DocumentsCache,
): Promise<ApproversDependenciesCache> {
  const cache: ApproversDependenciesCache = new Map();
  const keysToFetch = new Set<string>();

  for (const map of [approversMap, versionMap]) {
    for (const [docId, value] of map.entries()) {
      if (value.length <= 1) continue;

      const doc = documentsCache.get(docId);
      if (!doc) continue;

      keysToFetch.add(getApproversDependenciesKey(+doc.DocType.get_lookupId(), +doc.Collection.get_lookupId()));
    }
  }

  await Promise.all(
    [...keysToFetch].map(async (key) => {
      const [docType, collection] = key.split("_").map(Number);
      const result = await getItemsRequest({
        listName: approversDependenciesListName,
        filters: { DocType: docType, Collection: collection },
        sortOrder: { name: "Order0", asc: true },
      });
      cache.set(key, result.items);
    }),
  );

  return cache;
}

function getCachedDocumentsForFilteredMaps(
  filteredApproversMap: ApproversCardDocumentMapType,
  filteredVersionMap: ApproversCardDocumentMapType,
  documentsCache: DocumentsCache,
): DocumentItem[] {
  const results: DocumentItem[] = [];

  for (const docId of filteredApproversMap.keys()) {
    const doc = documentsCache.get(docId);
    if (doc) results.push(doc);
  }

  for (const docId of filteredVersionMap.keys()) {
    const doc = documentsCache.get(docId);
    if (doc) results.push(doc);
  }

  return results;
}

function buildApproversMapFromCards(
  cards: ListFieldsRecordType[typeof approversCardDocumentListName][],
): ApproversCardDocumentMapType {
  const map = new Map() as ApproversCardDocumentMapType;

  cards.forEach((approver) => {
    const docId = approver.Document;
    if (!docId) return;

    const existing = map.get(docId) ?? [];
    map.set(docId, [...existing, approver]);
  });

  return map;
}

/**
 * Flow 3: документы «На согласовании», где текущий пользователь — ответственный за пересмотр.
 * Дополнительно подтягиваются версии, где текущий пользователь был ПРЕДЫДУЩИМ ответственным
 * за пересмотр (LastResponsibleForRevisionEmail) — чтобы при замене ответственного
 * предыдущий не терял видимость документа, пока он ещё в работе.
 */
async function fetchRevisionResponsibleDocuments(userId: number, userEmail: string): Promise<{
  items: DocumentItem[];
  approversMap: ApproversCardDocumentMapType;
}> {
  const statusFilter = [docStatuses.approval, docStatuses.cancel, docStatuses.revision];
  const normalizedUserEmail = userEmail?.toLowerCase().trim();

  const [mainDocs, versionDocs, lastResponsibleVersionDocs] = await Promise.all([
    getItemsRequest({
      listName: myDocumentsListName,
      filters: { ResponsibleForRevision: userId, Status: statusFilter },
    }),
    getItemsRequest({
      listName: versionsByDocumentListName,
      filters: { ResponsibleForRevision: userId, Status: statusFilter },
    }),
    normalizedUserEmail
      ? getItemsRequest({
        listName: versionsByDocumentListName,
        filters: { LastResponsibleForRevisionEmail: normalizedUserEmail, Status: statusFilter },
      })
      : Promise.resolve({ items: [] as ListFieldsRecordType[typeof versionsByDocumentListName][] }),
  ]);

  // Приоритет: если у главного документа есть версия (где текущий пользователь
  // тот же ответственный за пересмотр) — показываем версию, главный скрываем.
  const mainDocIdsWithVersion = new Set(
    versionDocs.items.map((v) => +v.MainDocument.get_lookupId()),
  );
  const mainDocsFiltered = mainDocs.items.filter((d) => !mainDocIdsWithVersion.has(d.ID));

  const itemsById = new Map<number, DocumentItem>();
  [...mainDocsFiltered, ...versionDocs.items, ...lastResponsibleVersionDocs.items].forEach((item) =>
    itemsById.set(item.ID, item),
  );
  const items = [...itemsById.values()];

  if (!items.length) {
    return { items: [], approversMap: new Map() };
  }

  const docIds = items.map((item) => item.ID);
  const cardsResponse = await getItemsRequest({
    listName: approversCardDocumentListName,
    filters: { Document: docIds },
  });

  return {
    items,
    approversMap: buildApproversMapFromCards(cardsResponse.items),
  };
}

async function fetchReplacedDocumentResults(
  replacedApproversMap: ApproversCardDocumentMapType,
  replacedVersionMap: ApproversCardDocumentMapType,
): Promise<{ items: DocumentItem[]; filteredMap: ApproversCardDocumentMapType }> {
  const items: DocumentItem[] = [];
  const replacedDocIds = [...replacedApproversMap.keys()];
  const replacedVersionIds = [...replacedVersionMap.keys()];

  await Promise.all([
    replacedDocIds.length > 0
      ? getItemsRequest({
        listName: myDocumentsListName,
        filters: { ID: replacedDocIds },
      }).then((result) => {
        items.push(...result.items);
      })
      : Promise.resolve(),
    replacedVersionIds.length > 0
      ? getItemsRequest({
        listName: versionsByDocumentListName,
        filters: { ID: replacedVersionIds },
      }).then((result) => {
        items.push(...result.items);
      })
      : Promise.resolve(),
  ]);

  return {
    items,
    filteredMap: mergeMaps(replacedApproversMap, replacedVersionMap),
  };
}

function* getWorkDocumentsSaga(action: PayloadAction<GetSagaProps>) {
  try {
    const response: CurrentUserType = yield select(selectCurrentUser);
    const currentUserId = response.id;

    // Flow 1, список замещаемых и Flow 3 — независимы, загружаем параллельно
    const [replacedResponse, myMaps, revisionResponsibleFetch]: [
      {
        items: ListFieldsRecordType[typeof ReplacedListName][];
        listName: typeof ReplacedListName;
        nextPage: number | null;
        count: number;
      },
      {
        approversCardDocumentsMap: ApproversCardDocumentMapType;
        versionCardDocumentsMap: ApproversCardDocumentMapType;
      },
      { items: DocumentItem[]; approversMap: ApproversCardDocumentMapType },
    ] = yield all([
      call<any>(getItemsRequest, {
        listName: ReplacedListName,
        filters: { WhoReplaced: currentUserId },
      }),
      call(createMapByApproversCard, [currentUserId]),
      call(fetchRevisionResponsibleDocuments, currentUserId, response.email),
    ]);

    const replacedUserIds = replacedResponse.items.map((item) => Number(item.WhomReplaced.get_lookupId()));
    const { approversCardDocumentsMap: myApproversMap, versionCardDocumentsMap: myVersionMap } = myMaps;
    console.log("myApproversMap: ", myApproversMap);

    // Батч-загрузка документов и dependencies для filterDocByCurrentUser (вместо N+1 в processData)
    const {
      cache: myDocumentsCache,
      nextPage: myDocumentsNextPage,
    }: {
      cache: DocumentsCache;
      nextPage: number | null;
    } = yield call(fetchDocumentsByIds, myApproversMap, myVersionMap);

    const myDependenciesCache: ApproversDependenciesCache = yield call(
      fetchApproversDependenciesCache,
      myApproversMap,
      myVersionMap,
      myDocumentsCache,
    );

    const [filteredMyApproversMap, filteredMyVersionMap]: ApproversCardDocumentMapType[] = yield all([
      call(filterDocByCurrentUser, myApproversMap, [currentUserId], myDocumentsCache, myDependenciesCache),
      call(filterDocByCurrentUser, myVersionMap, [currentUserId], myDocumentsCache, myDependenciesCache),
    ]);

    // Flow 2: документы замещаемых — параллельно для каждого replacedUserId
    const replacedMapsResults: {
      approversCardDocumentsMap: ApproversCardDocumentMapType;
      versionCardDocumentsMap: ApproversCardDocumentMapType;
    }[] = yield all(replacedUserIds.map((replacedUserId) => call(createMapByReplacedApproversCard, replacedUserId)));

    let replacedApproversMap: ApproversCardDocumentMapType = new Map();
    let replacedVersionMap: ApproversCardDocumentMapType = new Map();

    for (const { approversCardDocumentsMap, versionCardDocumentsMap } of replacedMapsResults) {
      replacedApproversMap = mergeMaps(replacedApproversMap, approversCardDocumentsMap);
      replacedVersionMap = mergeMaps(replacedVersionMap, versionCardDocumentsMap);
    }

    const hasNoDocuments =
      !filteredMyApproversMap.size &&
      !filteredMyVersionMap.size &&
      !replacedApproversMap.size &&
      !replacedVersionMap.size &&
      !revisionResponsibleFetch.items.length;

    if (hasNoDocuments) {
      yield put(
        getItemsSuccess({
          listName: myWorksDocumentsListName,
          items: [],
          count: 0,
          nextPage: null,
        }),
      );
      return;
    }

    const myFilteredMap = mergeMaps(filteredMyApproversMap, filteredMyVersionMap);
    const myResults = getCachedDocumentsForFilteredMaps(filteredMyApproversMap, filteredMyVersionMap, myDocumentsCache);

    // Flow 1, Flow 2 и Flow 3 — параллельно; затем сборка approvers для Flow 2
    const [myDocumentsWithApprovers, replacedFetchResult, revisionResponsibleWithApprovers]: [
      DocumentsWithApproversTypeRecord[],
      { items: DocumentItem[]; filteredMap: ApproversCardDocumentMapType },
      DocumentsWithApproversTypeRecord[],
    ] = yield all([
      call(getDocumentsWithApprovers, myResults, myFilteredMap, currentUserId, false),
      call(fetchReplacedDocumentResults, replacedApproversMap, replacedVersionMap),
      call(
        getDocumentsWithApprovers,
        revisionResponsibleFetch.items,
        revisionResponsibleFetch.approversMap,
        currentUserId,
        false,
      ),
    ]);

    const replacedDocumentsWithApprovers: DocumentsWithApproversTypeRecord[] = yield call(
      getDocumentsWithApprovers,
      replacedFetchResult.items,
      replacedFetchResult.filteredMap,
      currentUserId,
      true,
    );

    const replacedDocumentIds = new Set(replacedDocumentsWithApprovers.map((d) => d.ID));
    const deduplicatedMyDocuments = myDocumentsWithApprovers.filter((d) => !replacedDocumentIds.has(d.ID));

    const existingDocumentIds = new Set([
      ...deduplicatedMyDocuments.map((d) => d.ID),
      ...replacedDocumentsWithApprovers.map((d) => d.ID),
    ]);
    const deduplicatedRevisionResponsibleDocuments = revisionResponsibleWithApprovers.filter(
      (d) => !existingDocumentIds.has(d.ID),
    );

    const allDocuments = applyMainVersionPriority([
      ...deduplicatedMyDocuments,
      ...replacedDocumentsWithApprovers,
      ...deduplicatedRevisionResponsibleDocuments,
    ]);

    yield put(
      getItemsSuccess({
        listName: myWorksDocumentsListName,
        items: allDocuments,
        count: allDocuments.length,
        nextPage: myDocumentsNextPage,
      }),
    );
  } catch (error) {
    console.log(error);
    yield put(getMyDocumentsInfoFailure(error));
  }
}

// WhoReplaced — кто передал права согласования (замещающий)
// WhomReplaced — кого замещают

async function filterDocByCurrentUser(
  approversCardDocumentsMap: ApproversCardDocumentMapType,
  ids: number[],
  documentsCache: DocumentsCache,
  dependenciesCache: ApproversDependenciesCache,
) {
  if (approversCardDocumentsMap.size === 0) return approversCardDocumentsMap;

  const promises = [];

  for (const [docId, value] of approversCardDocumentsMap.entries()) {
    promises.push(processData(docId, value, approversCardDocumentsMap, ids, documentsCache, dependenciesCache));
  }

  await Promise.allSettled(promises);

  return approversCardDocumentsMap;
}

async function processData(
  docId: number,
  value: ListFieldsRecordType[typeof approversCardDocumentListName][],
  map: ApproversCardDocumentMapType,
  ids: number[],
  documentsCache: DocumentsCache,
  dependenciesCache: ApproversDependenciesCache,
) {
  let approversSet = new Set<number>();

  const cachedDoc = documentsCache.get(docId);
  const doc: { items: DocumentItem[] } = cachedDoc ? { items: [cachedDoc] } : { items: [] };

  if (!doc.items.length) throw new Error("Не найдено ни одного документа в списках версий и в главном списке");

  if (value.length > 1) {
    const dependenciesKey = getApproversDependenciesKey(
      +doc.items[0].DocType.get_lookupId(),
      +doc.items[0].Collection.get_lookupId(),
    );
    const docTypesItems = dependenciesCache.get(dependenciesKey) ?? [];

    const sortedDocTypes = docTypesItems.sort((a, b) => Number(a.Order0) - Number(+b.Order0));

    const docTypesIds = sortedDocTypes.map((m) => +m.Group.get_lookupId());

    const filteredApproversWithoutCreated = value.filter((v) => +v.Group.get_lookupId() !== 6);

    const sortedValueByGroup = _.orderBy(filteredApproversWithoutCreated, (item) =>
      docTypesIds.indexOf(+item.Group.get_lookupId()),
    );

    if (sortedValueByGroup.length === 0) {
      map.delete(docId);
    } else {
      sortedValueByGroup.some((s) => {
        s.Approvers.forEach((a) => approversSet.add(+a.get_lookupId()));

        if (s.Queue) {
          return true;
        } else {
          return false;
        }
      });
      map.set(docId, sortedValueByGroup);
    }
  } else {
    map.set(docId, value);
  }

  const createdApproveDocByUser = value.find((v) => +v.Group.get_lookupId() === 6);

  const isFoundRevisionDoc = Number(createdApproveDocByUser?.Statuses[0].get_lookupId()) === 3;

  let currentUserInCreatedApproveDocByUser: number[] =
    createdApproveDocByUser?.Approvers.map((a) => +a.get_lookupId()) || [];
  if (createdApproveDocByUser?.Editor.get_lookupId() && Number(createdApproveDocByUser?.Editor.get_lookupId())) {
    currentUserInCreatedApproveDocByUser = [
      ...currentUserInCreatedApproveDocByUser,
      ...[Number(createdApproveDocByUser?.Editor.get_lookupId())],
    ];
  }
  const isFoundCurrentUser = ids.some((id) => currentUserInCreatedApproveDocByUser.includes(id));

  if (isFoundRevisionDoc || isFoundCurrentUser) {
    if (isFoundCurrentUser) {
      return;
    } else {
      map.delete(docId);
      return;
    }
  }

  const hasAnyApprover = ids.some((id) => approversSet.has(id));
  if (!hasAnyApprover) {
    map.delete(docId);
  }
}

function* createMapByApproversCard(userIds: number[]) {
  // console.log("userIds: ", userIds);
  const approversCardDocumentProps = {
    listName: approversCardDocumentListName,
    filters: { Approvers: userIds, Group: 6 },
  };

  const approversCardDocumentProps_2 = {
    listName: approversCardDocumentListName,
    filters: { Approvers: userIds, Queue: true },
  };

  const [approversCardDocuments, approversCardDocuments_2]: [
    {
      items: ListFieldsRecordType[typeof approversCardDocumentListName][];
      listName: typeof approversCardDocumentListName;
      nextPage: number | null;
      count: number;
    },
    {
      items: ListFieldsRecordType[typeof approversCardDocumentListName][];
      listName: typeof approversCardDocumentListName;
      nextPage: number | null;
      count: number;
    },
  ] = yield all([
    call<any>(getItemsRequest, approversCardDocumentProps),
    call<any>(getItemsRequest, approversCardDocumentProps_2),
  ]);

  const itemsFinally = [...approversCardDocuments.items, ...approversCardDocuments_2.items];

  const items = _.uniq(itemsFinally.map((item) => item.Document));
  
  const approversCardDocumentsMap = new Map() as ApproversCardDocumentMapType;
  const versionCardDocumentsMap = new Map() as ApproversCardDocumentMapType;
  
  if (!items.length) return { approversCardDocumentsMap, versionCardDocumentsMap };

  const approversCardDocumentProps2 = {
    listName: approversCardDocumentListName,
    filters: { Document: items },
  };

  const approversCardDocuments2: {
    items: ListFieldsRecordType[typeof approversCardDocumentListName][];
    listName: typeof approversCardDocumentListName;
    nextPage: number | null;
    count: number;
  } = yield call<any>(getItemsRequest, approversCardDocumentProps2);


  if (!approversCardDocuments2.items.length && !approversCardDocuments.items.length)
    return { approversCardDocumentsMap, versionCardDocumentsMap };

  // console.log("approversCardDocuments2", approversCardDocuments2);

  approversCardDocuments2.items.forEach((approver) => {
    //console.log('approver: ', approver);
    const docId = approver.Document;

    if (!docId) {
      console.error("Документ не найден, или был не указан в ApproversCard");
      return;
    }

    if (approver.VersionDocument) {
      if (versionCardDocumentsMap.has(docId)) {
        let tempApprovers = versionCardDocumentsMap.get(docId) ?? [];
        versionCardDocumentsMap.set(docId, [...tempApprovers, approver]);
      } else {
        versionCardDocumentsMap.set(docId, [approver]);
      }
    } else {
      if (approversCardDocumentsMap.has(docId)) {
        let tempApprovers = approversCardDocumentsMap.get(docId) ?? [];
        approversCardDocumentsMap.set(docId, [...tempApprovers, approver]);
      } else {
        approversCardDocumentsMap.set(docId, [approver]);
      }
    }
  });

  const approversCardDocumentsFilterMapByUser = filterMapByUser(approversCardDocumentsMap, userIds);
  const versionCardDocumentsFilterMapByUser = filterMapByUser(versionCardDocumentsMap, userIds);

  return {
    approversCardDocumentsMap: approversCardDocumentsFilterMapByUser,
    versionCardDocumentsMap: versionCardDocumentsFilterMapByUser,
  };
}

/**
 * Поиск документов для замещаемого пользователя.
 * Ищем только карточки где Queue:true — то есть сейчас очередь именно у него.
 * Затем подтягиваем все карточки этих документов для полной картины согласования.
 */
function* createMapByReplacedApproversCard(replacedUserId: number) {
  const approversCardDocumentsMap = new Map() as ApproversCardDocumentMapType;
  const versionCardDocumentsMap = new Map() as ApproversCardDocumentMapType;

  const queueCards: {
    items: ListFieldsRecordType[typeof approversCardDocumentListName][];
    listName: typeof approversCardDocumentListName;
    nextPage: number | null;
    count: number;
  } = yield call<any>(getItemsRequest, {
    listName: approversCardDocumentListName,
    filters: { Approvers: [replacedUserId], Queue: true },
  });

  if (!queueCards.items.length) return { approversCardDocumentsMap, versionCardDocumentsMap };

  const docIds = _.uniq(queueCards.items.map((item) => item.Document).filter(Boolean));

  if (!docIds.length) return { approversCardDocumentsMap, versionCardDocumentsMap };

  const allCards: {
    items: ListFieldsRecordType[typeof approversCardDocumentListName][];
    listName: typeof approversCardDocumentListName;
    nextPage: number | null;
    count: number;
  } = yield call<any>(getItemsRequest, {
    listName: approversCardDocumentListName,
    filters: { Document: docIds },
  });

  allCards.items.forEach((approver) => {
    const docId = approver.Document;
    if (!docId) return;

    if (approver.VersionDocument) {
      const existing = versionCardDocumentsMap.get(docId) ?? [];
      versionCardDocumentsMap.set(docId, [...existing, approver]);
    } else {
      const existing = approversCardDocumentsMap.get(docId) ?? [];
      approversCardDocumentsMap.set(docId, [...existing, approver]);
    }
  });

  const filteredApproversMap = filterMapByReplacedUser(approversCardDocumentsMap, replacedUserId);
  const filteredVersionMap = filterMapByReplacedUser(versionCardDocumentsMap, replacedUserId);

  return {
    approversCardDocumentsMap: filteredApproversMap,
    versionCardDocumentsMap: filteredVersionMap,
  };
}

function filterMapByUser(data: ApproversCardDocumentMapType, userIds: number[]) {
  const tempMap = new Map(data);

  tempMap.forEach((cardArray, key) => {
    const isUserApprovers = cardArray.some((card) =>
      card.Approvers.some((approver) => userIds.includes(+approver.get_lookupId())),
    );

    if (!isUserApprovers) {
      tempMap.delete(key);
    }
  });

  return tempMap;
}

/**
 * Оставляет только документы, в которых замещаемый пользователь
 * является текущим активным согласующим (Queue:true).
 */
function filterMapByReplacedUser(data: ApproversCardDocumentMapType, replacedUserId: number) {
  const tempMap = new Map(data);

  tempMap.forEach((cardArray, key) => {
    const isReplacedUserInQueue = cardArray.some(
      (card) => card.Queue && card.Approvers.some((a) => +a.get_lookupId() === replacedUserId),
    );

    if (!isReplacedUserInQueue) {
      tempMap.delete(key);
    }
  });

  return tempMap;
}

type ApproversType = { Reacted: UserFields; Status: LookupFields; Author: UserFields };

async function prefetchApproversDependenciesForDocuments(
  documents: DocumentItem[],
  approversCards: ApproversCardDocumentMapType,
): Promise<ApproversDependenciesCache> {
  const cache: ApproversDependenciesCache = new Map();
  const keysToFetch = new Set<string>();

  for (const document of documents) {
    if (!approversCards.has(document.ID)) continue;

    const approvers = approversCards.get(document.ID) || [];
    if (approvers.length <= 1) continue;

    keysToFetch.add(getApproversDependenciesKey(+document.DocType.get_lookupId(), +document.Collection.get_lookupId()));
  }

  await Promise.all(
    [...keysToFetch].map(async (key) => {
      const [docType, collection] = key.split("_").map(Number);
      const result = await getItemsRequest({
        listName: approversDependenciesListName,
        filters: { DocType: docType, Collection: collection },
        sortOrder: { name: "Order0", asc: true },
      });
      cache.set(key, result.items);
    }),
  );

  return cache;
}

/**
 * @param isReplacedFlow — true если документы из потока замещения.
 *   Все документы этого потока помечаются isReplaced:true без доп. проверок,
 *   т.к. фильтрация по очереди уже выполнена в createMapByReplacedApproversCard.
 */
async function getDocumentsWithApprovers(
  documents: DocumentItem[],
  approversCards: ApproversCardDocumentMapType,
  currentUserId: number,
  isReplacedFlow: boolean,
) {
  const newDocuments: DocumentsWithApproversTypeRecord[] = [];
  const dependenciesCache = await prefetchApproversDependenciesForDocuments(documents, approversCards);

  // console.log("documents: ", documents);
  // console.log("approversCards: ", approversCards);
  // console.log("currentUserId: ", currentUserId);
  // console.log("isReplacedFlow: ", isReplacedFlow);

  for (const document of documents) {
    const docId = document.ID;

    let filteredApprovers: ApproversType[] = [];

    if (approversCards.has(docId)) {
      let approvers = approversCards.get(docId) || [];

      if (approversCards.has(docId) && !approvers.length) {
        console.warn("has key but empty approvers", docId, document);
      }

      if (approvers.length > 1) {
        const dependenciesKey = getApproversDependenciesKey(
          +document.DocType.get_lookupId(),
          +document.Collection.get_lookupId(),
        );
        const approversByTypeItems = dependenciesCache.get(dependenciesKey) ?? [];

        const approversByTypeMap = approversByTypeItems.reduce((acc: Map<number, ApproversDependenciesType>, cur) => {
          acc.set(+cur.Group.get_lookupId(), cur);
          return acc;
        }, new Map());

        filteredApprovers = approvers.reduce((acc, approverCard) => {
          if (+approverCard.Group.get_lookupId() === 6 && +approverCard.Statuses[0].get_lookupId() !== 3) return acc;

          const isHasInApproversByTypeMap = approversByTypeMap.has(+approverCard.Group.get_lookupId());

          if (isHasInApproversByTypeMap) {
            const obj: ApproversType = {
              Status: approverCard.Statuses[0],
              Reacted: (approverCard.Approved || approverCard.Denied) ?? approverCard.Approvers[0],
              Author: approverCard.Author,
            };
            acc.push(obj);
          }

          return acc;
        }, [] as ApproversType[]);
      } else {
        const obj: ApproversType = {
          Status: approvers[0].Statuses[0],
          Reacted: (approvers[0].Approved || approvers[0].Denied) ?? approvers[0].Approvers[0],
          Author: approvers[0].Author,
        };

        filteredApprovers.push(obj);
      }

      const isReplaced = isReplacedFlow;

      if ("MainDocument" in document && document.MainDocument) {
        newDocuments.push({
          ID: document.ID,
          Code: `${document.Code}.${document.Index}`,
          Status: document.Status,
          Title: document.Title,
          VersionID: +document.MainDocument.get_lookupId(),
          Collection: document.Collection,
          Department: document.Department,
          DocType: document.DocType,
          SentForApproval: approversCards.get(docId)![0].Created,
          ApproversList: filteredApprovers,
          isReplaced,
        });
      } else {
        newDocuments.push({
          ID: document.ID,
          Code: `${document.Code}.${document.Index}`,
          Status: document.Status,
          Title: document.Title,
          VersionID: 0,
          Collection: document.Collection,
          Department: document.Department,
          DocType: document.DocType,
          SentForApproval: approversCards.get(docId)![0].Created,
          ApproversList: filteredApprovers,
          isReplaced,
        });
      }
    }
  }

  // console.log("newDocuments: ", newDocuments);

  return newDocuments;
}

export function* workDocumentsInfoWatcher() {
  yield takeLatest(getWorkDocumentsInfo.toString(), getWorkDocumentsSaga);
}
